import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import AdminToast from '../admin/AdminToast';
import ApprenantEvaluationsSection from './ApprenantEvaluationsSection';
import ApprenantInscriptionAcademicSection from './ApprenantInscriptionAcademicSection';
import { ApprenantPaginatedList } from './ApprenantPaginatedSection';
import ApprenantSectionCard from './ApprenantSectionCard';
import { useRegisterPortalRefresh } from '../../contexts/PortalRefreshContext';
import ApprenantPageShell from './ApprenantPageShell';
import LucideSymbolIcon from '../icons/LucideSymbolIcon';
import { fetchInscription, fetchInscriptionPoste, recuPdfApiPath } from '../../services/academiaApprenantService';
import { downloadAuthenticatedFile } from '../../services/api';
import { formatMoney } from '../../utils/currency';
import {
  buildInscriptionPaymentState,
  formatInscriptionStatut,
  formatTransactionStatut,
  inscriptionFraisInscriptionDue,
  inscriptionResteAPayer,
  ligneFraisResteAPayer,
  toAmount,
} from '../../utils/inscriptionPresentation';

type LigneFrais = {
  id: string;
  typeFrais?: string;
  libelle?: string;
  montantCdf?: number | string;
  montantPayeCdf?: number | string;
};

type Transaction = {
  id: string;
  reference?: string;
  statut?: string;
  montant?: number | string;
};

type Recu = {
  id: string;
  numeroRecu?: string;
  montantCdf?: number | string;
  dateCreation?: string;
};

type Inscription = {
  id: string;
  statut?: string;
  montantTotalCdf?: number | string;
  montantPayeCdf?: number | string;
  matriculeSnapshot?: string;
  filiere?: { libelle?: string };
  cohorte?: { code?: string; libelle?: string };
  lignesFrais?: LigneFrais[];
  transactions?: Transaction[];
  recus?: Recu[];
};

type PaymentChoice = 'inscription' | 'total' | 'advance';

type ToastState = { message: string; variant: 'success' | 'error' } | null;

export default function ApprenantInscriptionDetailPanel() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [ins, setIns] = useState<Inscription | null>(null);
  const [poste, setPoste] = useState<{
    article?: { reference?: string; libelle?: string; salle?: { libelle?: string } };
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [avanceInput, setAvanceInput] = useState('');
  const [downloadingRecuId, setDownloadingRecuId] = useState<string | null>(null);
  const [toast, setToast] = useState<ToastState>(null);

  const showToast = useCallback((message: string, variant: 'success' | 'error') => {
    setToast({ message, variant });
  }, []);

  const dismissToast = useCallback(() => setToast(null), []);

  const load = useCallback(async () => {
    if (!id) return;
    setError(false);
    setLoading(true);
    try {
      const [data, posteData] = await Promise.all([fetchInscription(id), fetchInscriptionPoste(id)]);
      setIns(data);
      setPoste(posteData);
    } catch {
      setError(true);
      setIns(null);
      setPoste(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (loading) return;
    if (window.location.hash === '#evaluations') {
      document.getElementById('evaluations')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [loading]);

  useRegisterPortalRefresh(() => void load());

  const downloadRecu = async (recuId: string) => {
    setDownloadingRecuId(recuId);
    try {
      await downloadAuthenticatedFile(recuPdfApiPath(recuId), `recu-${recuId.slice(0, 8)}.pdf`);
      showToast('Reçu téléchargé avec succès.', 'success');
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Téléchargement du reçu impossible.', 'error');
    } finally {
      setDownloadingRecuId(null);
    }
  };

  const reste = ins ? inscriptionResteAPayer(ins) : 0;
  const fraisInscriptionReste = ins ? inscriptionFraisInscriptionDue(ins) : 0;
  const montantPaye = ins ? toAmount(ins.montantPayeCdf) : 0;
  const montantTotal = ins ? toAmount(ins.montantTotalCdf) : 0;
  const ratioPaye =
    montantTotal > 0 ? Math.min(100, Math.round((montantPaye / montantTotal) * 100)) : 0;
  const canPay = Boolean(ins && reste > 0 && ins.statut !== 'ANNULEE' && ins.statut !== 'CONFIRMEE');

  const subtitle = ins
    ? [
        ins.filiere?.libelle ?? 'Formation',
        ins.cohorte?.code ? `Cohorte ${ins.cohorte.code}` : null,
        ins.matriculeSnapshot ?? null,
      ]
        .filter(Boolean)
        .join(' · ')
    : null;

  const goToPayment = (choice: PaymentChoice) => {
    if (!ins) return;

    const state = buildInscriptionPaymentState(ins, {
      choice,
      customAmount: choice === 'advance' ? toAmount(avanceInput) : undefined,
      filiereLabel: ins.filiere?.libelle ?? '',
    });

    if (!state) {
      if (choice === 'advance') {
        showToast(
          avanceInput.trim()
            ? `Montant invalide. Saisissez entre 1 et ${formatMoney(reste, 'CDF')}.`
            : 'Indiquez le montant de votre avance.',
          'error',
        );
      } else if (choice === 'inscription') {
        showToast("Les frais d'inscription sont déjà réglés.", 'error');
      } else {
        showToast('Aucun montant à régler.', 'error');
      }
      return;
    }

    navigate(`/app/inscription/${ins.id}/paiement`, { state });
  };

  const avancePreview = useMemo(() => toAmount(avanceInput), [avanceInput]);

  return (
    <ApprenantPageShell
      title={ins?.filiere?.libelle ?? 'Détail inscription'}
      description={subtitle}
      sideCard={{
        label: canPay ? 'Reste à payer' : 'Montant total',
        value: loading ? '…' : formatMoney(canPay ? reste : ins?.montantTotalCdf, 'CDF'),
        helper: ins ? formatInscriptionStatut(ins.statut ?? '') : null,
        sideLinks: [
          { label: 'Mes cours', to: '/app/mes-cours', icon: 'list_alt' },
          { label: 'Accueil', to: '/app', icon: 'dashboard' },
        ],
      }}
      onRetry={() => void load()}
      isLoading={loading}
      isError={error || (!loading && !ins)}
      loadingMessage="Chargement de l'inscription…"
      errorMessage="Inscription introuvable ou inaccessible."
    >
      {ins ? (
        <>
          <p className="text-xs font-bold text-primary">
            <Link to="/app/mes-cours">← Mes cours</Link>
          </p>

          <ApprenantSectionCard
            variant="summary"
            icon="payments"
            title="Montant total de la formation"
            subtitle={formatInscriptionStatut(ins.statut ?? '')}
          >
            <p className="text-3xl font-black text-white">{formatMoney(ins.montantTotalCdf, 'CDF')}</p>
            <div className="mt-4">
              <div className="flex justify-between text-xs">
                <span className="text-white/80">Progression des paiements</span>
                <span className="font-bold">
                  {formatMoney(montantPaye, 'CDF')} / {formatMoney(montantTotal, 'CDF')}
                </span>
              </div>
              <div className="mt-2 h-2 overflow-hidden rounded-full bg-white/25">
                <div
                  className="h-full rounded-full bg-white transition-all"
                  style={{ width: `${ratioPaye}%` }}
                />
              </div>
            </div>
            {fraisInscriptionReste > 0.01 ? (
              <p className="mt-3 text-sm font-semibold text-amber-100">
                Frais d&apos;inscription à régler : {formatMoney(fraisInscriptionReste, 'CDF')}
              </p>
            ) : null}
            {reste > 0 ? (
              <p
                className={`text-sm font-semibold ${fraisInscriptionReste > 0.01 ? 'mt-1 text-white/85' : 'mt-3 text-amber-100'}`}
              >
                Solde restant : {formatMoney(reste, 'CDF')}
              </p>
            ) : (
              <p className="mt-3 text-sm font-semibold text-emerald-100">Formation soldée</p>
            )}
          </ApprenantSectionCard>

          {canPay ? (
            <section
              className="apprenant-inscription-payment"
              aria-labelledby="apprenant-payment-options-heading"
            >
              <header className="apprenant-inscription-payment__header">
                <span className="apprenant-inscription-payment__header-icon" aria-hidden>
                  <LucideSymbolIcon name="payments" tone="inherit" size={22} />
                </span>
                <div>
                  <h2 id="apprenant-payment-options-heading" className="apprenant-inscription-payment__title">
                    Options de paiement
                  </h2>
                  <p className="apprenant-inscription-payment__subtitle">
                    Choisissez comment régler votre formation. Les frais d&apos;inscription, de participation et
                    de brevet sont ventilés automatiquement sur votre dossier.
                  </p>
                </div>
              </header>

              <div className="apprenant-inscription-payment__body">
                <div className="apprenant-inscription-payment__options">
                  {fraisInscriptionReste > 0.01 ? (
                    <article className="apprenant-inscription-payment__option">
                      <div className="apprenant-inscription-payment__option-head">
                        <LucideSymbolIcon name="person_add" tone="primary" size={22} />
                        <div>
                          <h3 className="apprenant-inscription-payment__option-title">
                            Frais d&apos;inscription uniquement
                          </h3>
                          <p className="apprenant-inscription-payment__option-desc">
                            Première étape de souscription — distinct des frais de participation et de brevet.
                          </p>
                        </div>
                      </div>
                      <p className="apprenant-inscription-payment__option-amount">
                        {formatMoney(fraisInscriptionReste, 'CDF')}
                      </p>
                      <button
                        type="button"
                        className="apprenant-inscription-payment__btn apprenant-inscription-payment__btn--outline"
                        onClick={() => goToPayment('inscription')}
                      >
                        Payer les frais d&apos;inscription
                      </button>
                    </article>
                  ) : null}

                  <article className="apprenant-inscription-payment__option apprenant-inscription-payment__option--featured">
                    <div className="apprenant-inscription-payment__option-head">
                      <LucideSymbolIcon name="account_balance_wallet" tone="primary" size={22} />
                      <div>
                        <h3 className="apprenant-inscription-payment__option-title">
                          Totalité du solde formation
                        </h3>
                        <p className="apprenant-inscription-payment__option-desc">
                          Réglez l&apos;intégralité du montant restant (inscription, participation et brevet).
                        </p>
                      </div>
                    </div>
                    <p className="apprenant-inscription-payment__option-amount">{formatMoney(reste, 'CDF')}</p>
                    <button
                      type="button"
                      className="apprenant-inscription-payment__btn apprenant-inscription-payment__btn--primary"
                      onClick={() => goToPayment('total')}
                    >
                      Payer la totalité
                    </button>
                  </article>

                  <article className="apprenant-inscription-payment__option">
                    <div className="apprenant-inscription-payment__option-head">
                      <LucideSymbolIcon name="payments" tone="primary" size={22} />
                      <div>
                        <h3 className="apprenant-inscription-payment__option-title">Avance</h3>
                        <p className="apprenant-inscription-payment__option-desc">
                          Versez un montant partiel ; le solde pourra être complété plus tard.
                        </p>
                      </div>
                    </div>
                    <label className="apprenant-form-field">
                      <span className="apprenant-form-field__label">Montant de l&apos;avance (CDF)</span>
                      <input
                        type="number"
                        min={1}
                        max={reste}
                        step={1000}
                        className="academia-field w-full font-semibold tabular-nums"
                        value={avanceInput}
                        onChange={(e) => setAvanceInput(e.target.value)}
                        placeholder={`Ex. ${Math.min(reste, Math.max(10000, Math.round(reste * 0.25)))}`}
                      />
                    </label>
                    {avancePreview > 0.01 && avancePreview <= reste ? (
                      <p className="text-xs text-on-surface-variant">
                        Après ce paiement, il restera environ{' '}
                        <strong>{formatMoney(reste - avancePreview, 'CDF')}</strong>.
                      </p>
                    ) : null}
                    <button
                      type="button"
                      className="apprenant-inscription-payment__btn apprenant-inscription-payment__btn--outline"
                      onClick={() => goToPayment('advance')}
                    >
                      Payer cette avance
                    </button>
                  </article>
                </div>
              </div>
            </section>
          ) : null}

          {poste?.article ? (
            <ApprenantSectionCard
              variant="accent"
              icon="widgets"
              title="Votre poste informatique"
              subtitle="Attribué pour toute la durée de cette formation"
            >
              <p className="text-sm text-slate-700">
                <strong className="font-mono">{poste.article.reference}</strong>
                {poste.article.libelle ? ` — ${poste.article.libelle}` : ''}
                {poste.article.salle?.libelle ? ` (${poste.article.salle.libelle})` : ''}
              </p>
            </ApprenantSectionCard>
          ) : null}

          <ApprenantSectionCard icon="receipt_long" title="Détail des frais" subtitle="Ventilation par type de frais">
            <ApprenantPaginatedList
              items={ins.lignesFrais ?? []}
              emptyLabel="Aucune ligne de frais."
              renderItem={(l) => {
                const ligneReste = ligneFraisResteAPayer(l);
                const estPaye = ligneReste <= 0.01;
                return (
                  <li key={l.id} className="apprenant-inscription-fee-row">
                    <div>
                      <span className="font-medium text-slate-800">{l.libelle}</span>
                      {l.typeFrais === 'INSCRIPTION' ? (
                        <span className="ml-2 text-[10px] font-bold uppercase text-primary">
                          Souscription
                        </span>
                      ) : null}
                    </div>
                    <div className="text-right">
                      <span className="font-bold tabular-nums text-slate-900">
                        {formatMoney(l.montantCdf, 'CDF')}
                      </span>
                      <p className={`text-xs ${estPaye ? 'text-emerald-700' : 'text-amber-700'}`}>
                        {estPaye ? 'Réglé' : `Reste ${formatMoney(ligneReste, 'CDF')}`}
                      </p>
                    </div>
                  </li>
                );
              }}
            />
          </ApprenantSectionCard>

          {(ins.transactions?.length ?? 0) > 0 ? (
            <ApprenantSectionCard icon="payments" title="Paiements" subtitle="Historique des transactions">
              <ApprenantPaginatedList
                items={ins.transactions ?? []}
                emptyLabel="Aucune transaction."
                renderItem={(t) => (
                  <li key={t.id} className="apprenant-inscription-list-row">
                    <div>
                      <p className="font-medium text-slate-800">{t.reference ?? '—'}</p>
                      <p className="text-xs text-on-surface-variant">{formatTransactionStatut(t.statut ?? '')}</p>
                    </div>
                    <span className="font-bold tabular-nums">{formatMoney(t.montant, 'CDF')}</span>
                  </li>
                )}
              />
            </ApprenantSectionCard>
          ) : null}

          {(ins.recus?.length ?? 0) > 0 ? (
            <ApprenantSectionCard
              icon="description"
              title="Reçus de paiement"
              subtitle="Téléchargez vos justificatifs PDF"
              action={
                <Link to="/app/wallet?tab=historique" className="text-xs font-bold text-primary">
                  Historique complet →
                </Link>
              }
            >
              <ApprenantPaginatedList
                items={ins.recus ?? []}
                emptyLabel="Aucun reçu."
                renderItem={(r) => (
                  <li key={r.id} className="apprenant-inscription-list-row">
                    <div>
                      <p className="font-medium text-slate-800">{r.numeroRecu ?? `Reçu ${r.id.slice(0, 8)}`}</p>
                      <p className="text-xs text-on-surface-variant">
                        {r.dateCreation ? new Date(r.dateCreation).toLocaleDateString('fr-FR') : '—'}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-bold tabular-nums">{formatMoney(r.montantCdf, 'CDF')}</span>
                      <button
                        type="button"
                        disabled={downloadingRecuId === r.id}
                        onClick={() => void downloadRecu(r.id)}
                        className="inline-flex items-center gap-1 rounded-lg border border-primary/30 bg-primary/5 px-2.5 py-1 text-xs font-bold text-primary disabled:opacity-50"
                      >
                        <LucideSymbolIcon name="download" tone="inherit" size={14} />
                        {downloadingRecuId === r.id ? '…' : 'PDF'}
                      </button>
                    </div>
                  </li>
                )}
              />
            </ApprenantSectionCard>
          ) : null}

          {id ? <ApprenantEvaluationsSection inscriptionId={id} onToast={showToast} /> : null}
          {id ? <ApprenantInscriptionAcademicSection inscriptionId={id} onToast={showToast} /> : null}
        </>
      ) : null}

      <AdminToast
        message={toast?.message ?? null}
        variant={toast?.variant ?? 'success'}
        onClose={dismissToast}
      />
    </ApprenantPageShell>
  );
}
